import { appwriteConfig, databases } from '@/lib/appwrite/config';
import { backendBaseUrl, backendRequest } from '@/lib/backend/api';
import { Query } from 'appwrite';
import {
  calculateEscrowBreakdown,
  getSellerPayoutSummary,
} from './escrowCalculations';
import { getUserTrustScore, logTrustScoreEvent } from '@/lib/appwrite/trustScore';
import { paymentContactFromIdentifier } from './paymentContact';

export interface CreateOrderParams {
  orderId: string;
  orderAmount: number;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  productName?: string;
  returnUrl: string;
  notifyUrl: string;
  // New metadata fields
  customerId?: string;
  dealId?: string;
  streamId?: string;
  productId?: string;
  sellerId?: string;
  subscriptionType?: string; // NEW: For subscription payments
  dealAdjustmentId?: string;
}

export interface VerifyPaymentParams {
  orderId: string;
  paymentId?: string;
  signature?: string;
}

export interface CashfreeOrderResponse {
  return_url?: string;
  success: boolean;
  payment_session_id?: string;
  order_id?: string;
  order_status?: string;
  order_amount?: number;
  pricing?: DealAdjustmentPaymentPricing;
  error?: string;
}

export interface DealAdjustmentPaymentPricing {
  additionalAmount: number;
  buyerPlatformFee: number;
  buyerFeePercent: number;
  totalToPay: number;
  isFirstDealFree: boolean;
}

export interface CashfreeVerifyResponse {
  success: boolean; // FIXED: Changed from isValid to success to match function response
  isValid: boolean;
  orderDetails?: {
    order_id: string;
    order_status: string;
    order_amount: number;
    payment_method?: string;
    payment_id?: string;
    seller_id?: string;
  };
  error?: string;
  paymentId?: string; // Added for better response handling
  data?: any; // ADDED: To handle the data object from function response
}

const CASHFREE_WEBHOOK_URL = `${backendBaseUrl}/api/v1/payments/webhook`;

const normalizeDealAdjustmentPricing = (value: any): DealAdjustmentPaymentPricing => ({
  additionalAmount: Number(value?.additional_amount ?? value?.additionalAmount ?? 0),
  buyerPlatformFee: Number(value?.buyer_platform_fee ?? value?.buyerPlatformFee ?? 0),
  buyerFeePercent: Number(value?.buyer_fee_percent ?? value?.buyerFeePercent ?? 0),
  totalToPay: Number(value?.total_to_pay ?? value?.totalToPay ?? 0),
  isFirstDealFree: Boolean(value?.is_first_deal_free ?? value?.isFirstDealFree),
});

const createCashfreeOrderId = (prefix: string, entityId: string): string => {
  const safePrefix = prefix.replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 12) || "payment";
  const compactEntityId = entityId.replace(/[^a-zA-Z0-9]/g, "").slice(0, 16);
  return `${safePrefix}_${compactEntityId}_${Date.now().toString(36)}`.slice(0, 45);
};

export const createCashfreeOrder = async (params: CreateOrderParams): Promise<CashfreeOrderResponse> => {
  try {
    const contact = paymentContactFromIdentifier(params.customerEmail, params.customerPhone);
    if (!contact.phone) {
      return {
        success: false,
        error: "A valid mobile number is required to start the payment.",
      };
    }

    const result = await backendRequest<any>("/api/v1/payments/create-order", {
      method: "POST",
      body: JSON.stringify({
        orderId: params.orderId,
        orderAmount: params.orderAmount,
        customerId: params.customerId ?? params.customerEmail ?? params.orderId,
        customerName: params.customerName,
        customerEmail: contact.email || null,
        customerPhone: contact.phone,
        productName: params.productName,
        returnUrl: params.returnUrl,
        notifyUrl: params.notifyUrl || CASHFREE_WEBHOOK_URL,
        dealId: params.dealId,
        streamId: params.streamId,
        productId: params.productId,
        sellerId: params.sellerId,
        subscriptionType: params.subscriptionType,
        dealAdjustmentId: params.dealAdjustmentId,
      }),
    });

    return {
      success: true,
      order_id: result.order_id,
      payment_session_id: result.payment_session_id,
      order_status: result.order_status,
      order_amount: Number(result.order_amount ?? 0),
      return_url: result.return_url,
      pricing: result.pricing ? normalizeDealAdjustmentPricing(result.pricing) : undefined,
    };
  } catch (error) {
    console.error("Error creating Cashfree order:", error);
    throw new Error(error instanceof Error ? error.message : 'Failed to create order');
  }
};

export const verifyCashfreePayment = async (params: VerifyPaymentParams): Promise<CashfreeVerifyResponse | boolean> => {
  try {
    const result = await backendRequest<any>("/api/v1/payments/transactions/verify", {
      method: "POST",
      body: JSON.stringify(params),
    });

    if (!result.success) {
      return {
        success: false,
        isValid: false,
        error: result.error,
        orderDetails: undefined,
        paymentId: undefined,
      };
    }

    const orderDetails = result.orderDetails || result.data;
    const paymentId = result.paymentId || orderDetails?.payment_id;

    return {
      success: result.success,
      isValid: result.isValid !== undefined ? result.isValid : (orderDetails?.order_status === 'PAID'),
      orderDetails: orderDetails ? {
        order_id: orderDetails.order_id,
        order_status: orderDetails.order_status,
        order_amount: orderDetails.order_amount,
        payment_method: orderDetails.payment_method,
        payment_id: paymentId,
        seller_id: orderDetails.seller_id
      } : undefined,
      error: result.error,
      paymentId,
      data: result.data
    };
  } catch (error) {
    console.error("Error verifying payment:", error);
    return false;
  }
};

export const getCashfreePaymentDetails = async (orderId: string): Promise<CashfreeVerifyResponse> => {
  try {
    const result = await backendRequest<any>(`/api/v1/payments/transactions/${encodeURIComponent(orderId)}`);

    if (!result.success) {
      return {
        success: false,
        isValid: false,
        error: result.error,
        orderDetails: undefined,
        paymentId: undefined,
      };
    }

    const orderDetails = result.orderDetails || result.data;
    const paymentId = result.paymentId || orderDetails?.payment_id;

    return {
      success: result.success,
      isValid: result.isValid !== undefined ? result.isValid : (orderDetails?.order_status === 'PAID'),
      orderDetails: orderDetails ? {
        order_id: orderDetails.order_id,
        order_status: orderDetails.order_status,
        order_amount: orderDetails.order_amount,
        payment_method: orderDetails.payment_method || orderDetails.payment_details?.payment_method,
        payment_id: paymentId,
        seller_id: orderDetails.seller_id
      } : undefined,
      error: result.error,
      paymentId: paymentId,
      data: result.data
    };
  } catch (error) {
    console.error("Error getting payment details:", error);
    throw error;
  }
};

// Existing function to handle deal payments
export const createDealPaymentOrder = async (
  dealId: string,
  amount: number,
  customer: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
  },
  sellerId: string,
  returnUrl: string
): Promise<CashfreeOrderResponse> => {
  return createCashfreeOrder({
    orderId: createCashfreeOrderId('deal', dealId),
    orderAmount: amount,
    customerName: customer.name,
    customerEmail: customer.email || '',
    customerPhone: customer.phone || '',
    returnUrl: returnUrl,
    notifyUrl: `${CASHFREE_WEBHOOK_URL}?type=deal&dealId=${dealId}`,
    customerId: customer.id,
    dealId: dealId,
    sellerId: sellerId
  });
};

export const createDealAdjustmentPaymentOrder = async (
  dealId: string,
  adjustmentId: string,
  amount: number,
  customer: { id: string; name: string; email?: string; phone?: string },
  sellerId: string,
  returnUrl: string
): Promise<CashfreeOrderResponse> => createCashfreeOrder({
  orderId: createCashfreeOrderId("add", adjustmentId),
  orderAmount: amount,
  customerName: customer.name,
  customerEmail: customer.email || "",
  customerPhone: customer.phone || "",
  returnUrl,
  notifyUrl: `${CASHFREE_WEBHOOK_URL}?type=deal-adjustment&dealId=${dealId}&adjustmentId=${adjustmentId}`,
  customerId: customer.id,
  dealId,
  sellerId,
  dealAdjustmentId: adjustmentId,
});

export const getDealAdjustmentPaymentPricing = async (
  adjustmentId: string
): Promise<DealAdjustmentPaymentPricing> => {
  const result = await backendRequest<any>(
    `/api/v1/payments/deal-adjustments/${encodeURIComponent(adjustmentId)}/pricing`
  );
  const pricing = normalizeDealAdjustmentPricing(result);
  if (pricing.additionalAmount <= 0 || pricing.totalToPay < pricing.additionalAmount) {
    throw new Error("Unable to calculate this additional payment.");
  }
  return pricing;
};

// Existing function to handle stream purchases
export const createStreamPurchaseOrder = async (
  streamId: string,
  productId: string,
  amount: number,
  customer: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
  },
  sellerId: string,
  productName: string,
  returnUrl: string
): Promise<CashfreeOrderResponse> => {
  return createCashfreeOrder({
    orderId: createCashfreeOrderId('stream', streamId),
    orderAmount: amount,
    customerName: customer.name,
    customerEmail: customer.email || '',
    customerPhone: customer.phone || '',
    productName: productName,
    returnUrl: returnUrl,
    notifyUrl: `${CASHFREE_WEBHOOK_URL}?type=stream&streamId=${streamId}&productId=${productId}`,
    customerId: customer.id,
    streamId: streamId,
    productId: productId,
    sellerId: sellerId
  });
};

// NEW: Function to handle subscription payments
export const createSubscriptionPaymentOrder = async (
  userId: string,
  amount: number,
  points: number,
  customer: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
  },
  subscriptionType: string, // e.g., 'trust_points', 'premium_subscription'
  returnUrl: string
): Promise<CashfreeOrderResponse> => {
  return createCashfreeOrder({
    orderId: createCashfreeOrderId(subscriptionType, userId),
    orderAmount: amount,
    customerName: customer.name,
    customerEmail: customer.email || '',
    customerPhone: customer.phone || '',
    productName: `${subscriptionType} - ${points} points`,
    returnUrl: returnUrl,
    notifyUrl: `${CASHFREE_WEBHOOK_URL}?type=subscription&userId=${userId}&subscriptionType=${subscriptionType}&points=${points}`,
    customerId: customer.id,
    subscriptionType: subscriptionType
  });
};

// ============================================================
// ESCROW PAYMENT BREAKDOWN TYPES AND FUNCTIONS
// ============================================================

export interface PaymentBreakdown {
  originalAmount: number;
  discount: number;
  platformFee: number;
  platformFeePercent: number;
  finalAmount: number;
  canApplyDiscount: boolean;
  maxTrustDiscountAvailable: number;
  trustScorePoints: number;
  isFirstDealFree: boolean;
}

export interface SellerPayoutBreakdown {
  requestedAmount: number;
  platformFee: number;
  platformFeePercent: number;
  youWillReceive: number;
  isFirstDealFree: boolean;
}

/**
 * Check if this is the user's first completed deal
 * First deal free applies only once per user (as buyer OR seller)
 */
export async function checkIsFirstDeal(
  userId: string,
  excludeDealId?: string
): Promise<boolean> {
  try {
    // Check for any completed deals where user was buyer or seller
    const [buyerDeals, sellerDeals] = await Promise.all([
      databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.dealStage,
        [
          Query.equal('buyerId', userId),
          Query.equal('status', 'completed'),
          Query.limit(excludeDealId ? 2 : 1),
        ]
      ),
      databases.listDocuments(
        appwriteConfig.databaseId,
        appwriteConfig.dealStage,
        [
          Query.equal('sellerId', userId),
          Query.equal('status', 'completed'),
          Query.limit(excludeDealId ? 2 : 1),
        ]
      ),
    ]);

    // When displaying a completed deal, do not count that same deal as a
    // previous transaction. This keeps the UI aligned with the payout amount
    // the backend calculated before completing it.
    const isPreviousDeal = (deal: any) =>
      !excludeDealId || (deal.$id ?? deal.id) !== excludeDealId;
    return !buyerDeals.documents.some(isPreviousDeal) &&
      !sellerDeals.documents.some(isPreviousDeal);
  } catch (error) {
    console.error('Error checking first deal status:', error);
    // Default to false (not first deal) on error to avoid revenue loss
    return false;
  }
}

/**
 * Calculate seller payout breakdown
 * Shows what seller will receive after platform fees
 */
export async function calculateSellerPayout(
  requestedAmount: number,
  sellerId: string,
  excludeDealId?: string
): Promise<SellerPayoutBreakdown> {
  try {
    const isFirstDeal = await checkIsFirstDeal(sellerId, excludeDealId);
    return getSellerPayoutSummary(requestedAmount, isFirstDeal);
  } catch (error) {
    console.error('Error calculating seller payout:', error);
    // Return non-first-deal calculation on error
    return getSellerPayoutSummary(requestedAmount, false);
  }
}

/**
 * Calculate payment breakdown with platform fee and trust score discount
 * Uses the new escrow calculation formulas
 */
export async function calculatePaymentBreakdown(
  dealAmount: number,
  buyerId: string,
  applyTrustDiscount: boolean = false
): Promise<PaymentBreakdown> {
  try {
    // Get buyer's trust score for discount
    const trustScore = await getUserTrustScore(buyerId);
    const trustScorePoints = trustScore?.score ?? 0;

    // Calculate escrow breakdown with new formulas
    // First-deal-free applies to the seller payout only. Buyers pay the
    // normal platform fee on the initial payment and every order addition.
    const escrowBreakdown = calculateEscrowBreakdown(dealAmount, false);

    // Calculate platform fee using new formula
    const platformFee = escrowBreakdown.buyerPlatformFee;
    const platformFeePercent = escrowBreakdown.buyerFeePercent;

    // Calculate max trust discount available (10% of deal amount, capped by trust score / 10)
    const maxTrustDiscountAvailable = Math.min(
      trustScorePoints / 10,
      dealAmount * 0.1
    );

    // Apply discount only if user opted in
    const discount = applyTrustDiscount ? maxTrustDiscountAvailable : 0;

    // Calculate final amount: deal amount + platform fee - discount
    const finalAmount = dealAmount + platformFee - discount;

    return {
      originalAmount: dealAmount,
      discount,
      platformFee,
      platformFeePercent,
      finalAmount,
      canApplyDiscount: maxTrustDiscountAvailable > 0,
      maxTrustDiscountAvailable,
      trustScorePoints,
      isFirstDealFree: escrowBreakdown.isFirstDealFree,
    };
  } catch (error) {
    console.error('Error calculating payment breakdown:', error);

    // Return default breakdown on error (use non-first-deal rates)
    const fallbackBreakdown = calculateEscrowBreakdown(dealAmount, false);

    return {
      originalAmount: dealAmount,
      discount: 0,
      platformFee: fallbackBreakdown.buyerPlatformFee,
      platformFeePercent: fallbackBreakdown.buyerFeePercent,
      finalAmount: dealAmount + fallbackBreakdown.buyerPlatformFee,
      canApplyDiscount: false,
      maxTrustDiscountAvailable: 0,
      trustScorePoints: 0,
      isFirstDealFree: false,
    };
  }
}

/**
 * Deduct trust score points when discount is applied
 */
export async function deductTrustScoreForDiscount(
  userId: string,
  discountAmount: number
): Promise<boolean> {
  try {
    const pointsToDeduct = discountAmount * 10; // 1 rupee = 10 points

    await logTrustScoreEvent(
      userId,
      'discount_used',
      -pointsToDeduct,
      `Used ₹${discountAmount} discount on deal payment`
    );

    return true;
  } catch (error) {
    console.error('Error deducting trust score:', error);
    return false;
  }
}
